import { type Region } from '@/types/schema';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { deleteCookie, getCookie, setCookie } from 'cookies-next/client';

export const WIDGET_AUTH_LAUNCH_COOKIE = 'heidi-widget-auth-launch';

export type WidgetAuthLaunch = {
  t?: string;
  region?: Region;
  productName?: string;
};

const COOKIE_MAX_AGE_SECONDS = 10 * 60;

// Widget auth can bounce between Scribe and widget subdomains during login/logout.
// Use an explicit app-owned mapping instead of coupling this flow to Auth0 cookie envs.
const widgetAuthLaunchCookieDomains: Partial<Record<ENV_NAME, string>> = {
  [ENV_NAME.DEV]: '.heidihealth.com',
  [ENV_NAME.AZURE_DEV]: '.heidihealth.com',
  [ENV_NAME.STAGING]: '.heidihealth.com',
  [ENV_NAME.PROD]: '.heidihealth.com',
};

const getCompatibleCookieDomain = (environment: ENV_NAME) => {
  const cookieDomain = widgetAuthLaunchCookieDomains[environment];
  if (!cookieDomain || typeof window === 'undefined') return undefined;

  const hostname = window.location.hostname;
  const normalizedCookieDomain = cookieDomain.replace(/^\./, '');

  if (
    hostname === normalizedCookieDomain ||
    hostname.endsWith(`.${normalizedCookieDomain}`)
  ) {
    return cookieDomain;
  }

  return undefined;
};

const shouldUseSecureCookie = () => process.env.NEXT_PUBLIC_ENVIRONMENT !== 'localhost';

const getWidgetAuthLaunchCookieOptions = () => {
  const environment = getEnvironment();
  const domain = getCompatibleCookieDomain(environment);

  return {
    path: '/',
    ...(domain && { domain }),
    secure: shouldUseSecureCookie(),
    sameSite: 'lax' as const,
    maxAge: COOKIE_MAX_AGE_SECONDS,
  };
};

const encodeLaunch = (launch: WidgetAuthLaunch) =>
  encodeURIComponent(JSON.stringify(launch));

const decodeLaunch = (value: string): WidgetAuthLaunch | null => {
  try {
    return JSON.parse(decodeURIComponent(value)) as WidgetAuthLaunch;
  } catch {
    try {
      return JSON.parse(value) as WidgetAuthLaunch;
    } catch {
      return null;
    }
  }
};

export const writeWidgetAuthLaunchCookie = (launch: WidgetAuthLaunch) => {
  setCookie(
    WIDGET_AUTH_LAUNCH_COOKIE,
    encodeLaunch(launch),
    getWidgetAuthLaunchCookieOptions()
  );
};

export const readWidgetAuthLaunchCookie = (): WidgetAuthLaunch | null => {
  const value = getCookie(WIDGET_AUTH_LAUNCH_COOKIE);
  if (!value || typeof value !== 'string') return null;

  return decodeLaunch(value);
};

export const clearWidgetAuthLaunchCookie = () => {
  deleteCookie(WIDGET_AUTH_LAUNCH_COOKIE, getWidgetAuthLaunchCookieOptions());
};
